import { z } from 'zod';
import { CHECKOUT_PAYMENT_TYPES, PREORDER_PAYMENT_TYPES, DELIVERY_CHOICES, TAR_PROCESS_ENUM, EKHO_FINANCING_APPLICATION_SECTIONS, CREDIT_APP_FIELD_IDS } from '../../constants.js';
import { PreQualDecision } from '../../types/leads/PreQualSubmission.js';

/**
 * Property schemas used by more than one registry file, so two entries
 * describing the same domain value cannot drift apart.
 *
 * Each enum derives from the same constant the app filters on when deciding what
 * to render, which is what makes them safe to keep narrow: a newly shipped
 * payment option lands in the renderer and the schema at the same time, so there
 * is no window where a real value warns as invalid.
 *
 * Checkout and preorder payment options are separate enums rather than one wide
 * one, because the two sets are disjoint by construction —
 * `getPaymentConfigOptions` returns either `checkout.paymentConfig` or
 * `preorder.preorderPaymentConfig`, never a merge, and their schemas permit
 * different keys. A `co_` event carrying `PREORDER_RESERVATION` would be a bug,
 * so the schema should reject it rather than describe it as possible.
 */
/**
 * The services providers an order is attributed to, for tenant-level reporting.
 *
 * All optional, and callers must omit rather than send `''` for one that does
 * not resolve (convention §10): a warehouse deal with no inventory provider and
 * an id that failed to resolve are different facts, and an empty-string sentinel
 * makes them indistinguishable downstream.
 */
const SVP_IDS = {
    domainOwnerSvpId: z.string().optional(),
    sellerSvpId: z.string().optional(),
    inventorySvpId: z.string().optional(),
};
/**
 * Whether the program owner is a dealer, so Customer.io can route an internal
 * ops alert to `#<channel>-dealer` without re-deriving the fact from the seller
 * or the Company object.
 *
 * Deliberately a boolean rather than the stored `dealer | omni | access`
 * configuration value: the routing decision is binary, and the two non-dealer
 * arrangements — an OEM selling direct under Ekho's licence, and an OEM selling
 * through its own dealer network — differ in ways that have nothing to do with
 * which Slack channel an alert belongs in.
 *
 * Always present on the events that carry this context: a program owner that
 * cannot be classified — no id stored, nothing configured, or a failed lookup —
 * resolves to `false` rather than dropping the property, which is the channel
 * Customer.io would have picked from an absent flag anyway. Kept optional on the
 * schema so an event definition spreading this context is not forced to carry it
 * before its emitter does.
 */
const IS_DEALER = {
    isDealer: z.boolean().optional(),
};
/** A payment option a buyer can hold in regular checkout. */
const CHECKOUT_PAYMENT_OPTION = z.enum(CHECKOUT_PAYMENT_TYPES);
/** A reservation option a buyer can hold in preorder checkout. */
const PREORDER_PAYMENT_OPTION = z.enum(PREORDER_PAYMENT_TYPES);
/** Any fulfilment choice a buyer can hold. */
const DELIVERY_OPTION = z.enum(Object.values(DELIVERY_CHOICES));
/** How a buyer's title and registration paperwork is handled. */
const TAR_OPTION = z.enum(Object.values(TAR_PROCESS_ENUM));
/**
 * What the credit engine decided about a buyer's pre-qualification.
 *
 * Derived from `PreQualDecision`, the same union the engine returns, so the two
 * cannot drift. Deliberately narrower than `PREQUAL_OUTCOMES`, which carries a
 * `NOT_STARTED` member: that is a lead-level state, not a decision, and an event
 * reporting a decision can only fire once one has been made.
 *
 * Surfaces may soften how a denial reads to the buyer, and several do. The event
 * still reports the decision the engine made, because the copy is a presentation
 * choice that changes without the underlying verdict changing.
 */
const PREQUAL_OUTCOME = z
    .enum(Object.values(PreQualDecision))
    .describe('The credit decision the pre-qualification returned.');
/**
 * A section of the credit application. The stable keys rather than the labels
 * the constant maps to: those labels are headings rendered to the buyer, so
 * emitting them would tie a year of reporting to a copy change.
 *
 * Shared so checkout and Express describe the same form in the same terms; the
 * two collect the same sections through different UI, and a report that cannot
 * union them is a report about one surface pretending to be about the funnel.
 */
const CREDIT_APP_SECTION = z
    .enum(Object.keys(EKHO_FINANCING_APPLICATION_SECTIONS))
    .describe('Which section of the credit application.');
/** A single input on the credit application. The name only: these fields hold regulated data. */
z
    .enum(Object.values(CREDIT_APP_FIELD_IDS))
    .describe('Which credit-application input, by name only.');
/**
 * One item in a GA4 ecommerce payload. Product data only — this leg reaches Meta,
 * TikTok, Tracklution and GA4, so nothing about the buyer belongs here.
 */
const GA4_ITEM = z.object({
    item_id: z.string(),
    item_name: z.string(),
    item_brand: z.string().optional(),
    item_category: z.string().optional(),
    price: z.number(),
    quantity: z.number(),
    item_variant: z.string().optional(),
    item_list_name: z.string().optional(),
    index: z.number().optional(),
});
/**
 * The ad-bound ecommerce payload an event may carry alongside its properties,
 * passed as `opts.ecommerce` and mirroring `EcommercePayloadInput`.
 *
 * Declared per event via `ecommerceSchema` so this leg is as reviewable as the
 * properties are. It travels to external ad platforms rather than the CDP, which
 * makes it the payload most worth checking and, until now, the only one nothing
 * validated. Keys are camelCase here: the adapter maps them to GA4's snake_case
 * spelling on the way out.
 */
const ECOMMERCE_PAYLOAD = z
    .object({
    items: z.array(GA4_ITEM).describe('The vehicles and accessories the event concerns.'),
    transactionId: z.string().optional().describe('Order id, once one exists.'),
    value: z.number().optional().describe('Monetary value in `currency`.'),
    currency: z.string().optional().describe('ISO currency code.'),
    tax: z.number().optional(),
    shipping: z.number().optional(),
    affiliation: z.string().optional().describe('Selling entity, where the platform wants one.'),
    shippingTier: z.string().optional().describe('Fulfilment tier, for `add_shipping_info`.'),
    paymentType: z.string().optional().describe('Payment method, for `add_payment_info`.'),
    itemListName: z.string().optional(),
    objectId: z
        .string()
        .optional()
        .describe('The order or preorder id the dedup `event_id` is built from, so the browser and server copies of one placement collapse to a single conversion. Read by `trackEcommerceEvent`, not by the CDP legs.'),
})
    // Strict on purpose. A permissive object silently drops unknown keys, so adding
    // a buyer field here would reach the ad platforms with nothing warning about it.
    // Rejecting the unknown key is the only way this schema is a PII guard rather
    // than documentation of one.
    .strict();
/**
 * Which of a surface's addresses an event concerns.
 *
 * Shared so checkout and Express name the same three collection points the same
 * way. Express collects home, registration and shipping; checkout collects
 * registration, shipping and a prequal address. The union is the enum, and a
 * surface simply never emits the members it has no form for.
 */
const ADDRESS_FIELD = z
    .enum(['home', 'registration', 'shipping', 'prequal', 'fulfillment'])
    .describe('Which address on the page this is.');
/** The five inputs of the manual address form. */
const ADDRESS_SUBFIELD = z
    .enum(['line1', 'line2', 'city', 'state', 'zip'])
    .describe('Which input within the manual address form.');

export { ADDRESS_FIELD, ADDRESS_SUBFIELD, CHECKOUT_PAYMENT_OPTION, CREDIT_APP_SECTION, DELIVERY_OPTION, ECOMMERCE_PAYLOAD, IS_DEALER, PREORDER_PAYMENT_OPTION, PREQUAL_OUTCOME, SVP_IDS, TAR_OPTION };
//# sourceMappingURL=shared-fields.js.map
